import {
    AstNodeType,
    EnumComparisonOperator,
    FieldTypeCategory,
    flattenAst,
    LookupOperator,
    NumericMathOperator,
    StringComparisonOperator,
    StringOperationOperator,
    TimeComparisonOperator,
    TimeMathOperator,
} from '@thinkalpha/language-services';
import type {
    AstNode,
    BinaryOperationNode,
    CallNode,
    FunctionDef,
    KnownAstNode,
    ParentheticalNode,
    UnaryOperationNode,
} from '@thinkalpha/language-services';
import { container } from 'src/StaticContainer';

const _log = container.get('Logger').getSubLogger({ name: 'ts-compiler' });

export interface HoistedColumn {
    name: string;
    formula: string;
    subscript?: string;
    output?: string;
}

export interface CompilationResult {
    result: string;
    hoistedColumns: HoistedColumn[];
}

export interface CompilationOptions {
    sourceFunctionDefs?: FunctionDef[];
}

export const machineGeneratedPrefix = '__';

const isCallNode = (x: KnownAstNode): x is CallNode & { depth: number } => x.type === AstNodeType.call;

export const getFunctionDefs = (node: KnownAstNode) => {
    return flattenAst(node)
        .filter(isCallNode)
        .map((x) => x.functionDef)
        .filter((x): x is FunctionDef => !!x);
};

export function compile(node: KnownAstNode | null | undefined, options: CompilationOptions): CompilationResult {
    if (!node) return { result: '', hoistedColumns: [] };
    if (!node.valid) return { result: '', hoistedColumns: [] };

    if (!options.sourceFunctionDefs) {
        options.sourceFunctionDefs = getFunctionDefs(node);
    }

    // const { sourceFunctionDefs } = options;

    switch (node.type) {
        case AstNodeType.binaryOperation: {
            const { lhs: operand1, rhs: operand2, operator } = node as BinaryOperationNode;
            const compiledOperand1 = compile(operand1, options);
            const compiledOperand2 = compile(operand2, options);
            if (operator in LookupOperator) {
                if (operator === LookupOperator['.']) {
                    return {
                        result:
                            operand1.type === AstNodeType.call && !operand1.arguments.length // column indicator
                                ? `${compiledOperand1.result}.${compiledOperand2.result}`
                                : `bind(${compiledOperand1.result}, ${compiledOperand2.result})`,
                        hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                    };
                }
            }
            if (operator in TimeMathOperator && operand1.dataType === FieldTypeCategory.Timestamp) {
                if (operator === TimeMathOperator['+']) {
                    return {
                        result: `time_add(${compiledOperand1.result}, ${compiledOperand2.result})`,
                        hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                    };
                } else if (operator === TimeMathOperator['-']) {
                    return {
                        result: `time_diff(${compiledOperand1.result}, ${compiledOperand2.result})`,
                        hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                    };
                }
            }
            if (operator in TimeComparisonOperator && operand1.dataType === FieldTypeCategory.Timestamp) {
                switch (operator) {
                    case TimeComparisonOperator['<']:
                        return {
                            result: `time_smaller(${compiledOperand1.result}, ${compiledOperand2.result})`,
                            hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                        };
                    case TimeComparisonOperator['>']:
                        return {
                            result: `time_larger(${compiledOperand1.result}, ${compiledOperand2.result})`,
                            hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                        };
                }
            }
            if (operator in EnumComparisonOperator && operand1.dataType === FieldTypeCategory.Enum) {
                switch (operator) {
                    case EnumComparisonOperator['==']:
                        return {
                            result: `enum_eq(${compiledOperand1.result}, ${compiledOperand2.result})`,
                            hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                        };
                    case EnumComparisonOperator['!=']:
                        return {
                            result: `enum_not_eq(${compiledOperand1.result}, ${compiledOperand2.result})`,
                            hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                        };
                }
            }
            if (operator in StringComparisonOperator && operand1.dataType === FieldTypeCategory.String) {
                if (operator === StringComparisonOperator['like']) {
                    return {
                        result: `(${compiledOperand1.result} like ${compiledOperand2.result})`,
                        hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                    };
                }
                return {
                    result: `(str_cmp(${compiledOperand1.result}, ${compiledOperand2.result}) ${operator} 0)`,
                    hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                };
            }
            if (operator in StringOperationOperator && operand1.dataType === FieldTypeCategory.String) {
                switch (operator) {
                    case '+':
                        return {
                            result: `concat2(${compiledOperand1.result}, ${compiledOperand2.result})`,
                            hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                        };
                }
            }
            if (operator in NumericMathOperator && operand1.dataType === FieldTypeCategory.Double) {
                switch (operator) {
                    case NumericMathOperator['%']:
                        return {
                            result: `mod(${compiledOperand1.result}, ${compiledOperand2.result})`,
                            hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                        };
                    case NumericMathOperator['^']:
                        return {
                            result: `pow(${compiledOperand1.result}, ${compiledOperand2.result})`,
                            hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
                        };
                }
            }
            return {
                result: `(${compiledOperand1.result} ${operator} ${compiledOperand2.result})`,
                hoistedColumns: [...compiledOperand1.hoistedColumns, ...compiledOperand2.hoistedColumns],
            };
        }
        case AstNodeType.const: {
            const { value, dataType } = node;
            let res: string;
            switch (typeof value) {
                case 'string':
                    if (dataType === FieldTypeCategory.Regex) {
                        res = `'%${value}'`;
                    } else {
                        res = `'$${value}'`;
                    }
                    break;
                case 'number':
                    res = `${value}`;
                    break;
                case 'object':
                    if (value === null) throw new Error('null constant encountered');
                    if ('period' in value) {
                        res = `_${value.value}${value.period}`;
                    } else {
                        throw new Error('Unsupported value type found');
                    }
                    break;
                default:
                    throw new Error(`Unsupported value type found: ${typeof value}`);
            }
            return {
                result: res,
                hoistedColumns: [],
            };
        }
        case AstNodeType.call: {
            const {
                source,
                functionDef,
                ranges: { eachArgument: _argumentRanges },
            } = node;
            const args: (KnownAstNode | null)[] = node.arguments;

            if (!functionDef) throw new Error('No function definition found');

            // const params = functionDef.params;
            const compiledArgs = args.map((x, _i): CompilationResult => {
                // const _param = params[i];
                return compile(x, options);
            });
            const formula =
                functionDef?.symbol ??
                source + (args.length ? `(${compiledArgs.map((x) => x.result).join(', ')})` : '');
            return {
                result: formula,
                hoistedColumns: compiledArgs.flatMap((x) => x.hoistedColumns),
            };
        }
        case AstNodeType.unaryOperation: {
            const { operator, operand } = node as UnaryOperationNode;
            const compiledOperand = compile(operand, options);
            return {
                result: `(${operator}${compiledOperand.result})`,
                hoistedColumns: compiledOperand.hoistedColumns,
            };
        }
        case AstNodeType.paren: {
            const { content } = node as ParentheticalNode;
            const compiledContent = compile(content, options);
            return {
                result: `(${compiledContent.result})`,
                hoistedColumns: compiledContent.hoistedColumns,
            };
        }
        default:
            throw new Error(`Unknown AST node type: ${(node as AstNode).type}`);
    }
}
